/*
  Môdern Space Gallery — Design My Space
  Config-driven routing wizard. No persistent customer/project state yet —
  everything lives in memory for the page view. Built so a later version can
  swap `saveState()`/`loadState()` for real persistence (Projects, saved
  rooms) without touching the step-rendering or routing logic below.

  Sections of this file, in order:
    1. CONFIG          — rooms, goals, conditional question sets
    2. RECOMMENDATIONS — copy + routing matrix, keyed by "goal|conditionalValue"
    3. LAYOUT ROUTING  — room-aware special case for "Solve a problem → Layout"
    4. ANALYTICS       — GA4 event helper, matches snippets/affiliate-tracking.liquid
    5. STATE + RENDER  — the wizard itself, including step-context labels and
                          the recommendation-screen selection summary
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-dms-root]');
  if (!root) return;
  var panel = root.querySelector('[data-dms-panel]');
  if (!panel) return;

  var configEl = document.querySelector('[data-dms-config]');
  var CONFIG;
  try {
    CONFIG = JSON.parse(configEl.textContent);
  } catch (e) {
    CONFIG = null;
  }
  if (!CONFIG) return;

  /* ==========================================================================
     2. RECOMMENDATIONS — copy + routing, keyed by "<goal>|<conditionalValue>"
     Edit this object to change what Design My Space says or where it sends
     people. "{room}" in body/heading is replaced with the room's natural
     phrase (e.g. "your living room", "your whole home").

     destination_type: "tool" | "guide" | "concierge" — sent to analytics so
     guide clicks/impressions can be reported on separately from tool
     clicks/impressions.
     tool: a short machine-readable identifier, sent as a GA4 property
     (e.g. "modern_match", "rug_guide", "concierge").
     action: "link" (navigate to url, default) or "concierge" (open the
     on-page Design Concierge chat instead of navigating).
     flagged: true marks a destination chosen as a temporary safe fallback.
     Price is intentionally never included in this copy — if the destination
     is a paid product, its own page shows the price after the click.
     ========================================================================== */
  var RECOMMENDATIONS = {
    'whole-room|yes': {
      heading: "Let's shop for your space.",
      body: "You know your style and you're ready to design {room} from the ground up. Môdern Match can put together a coordinated set — furniture, layout logic, and finishing touches — built around your style and budget.",
      cta: 'Shop for Your Space',
      url: '/pages/modern-match',
      tool: 'modern_match',
      destination_type: 'tool'
    },
    'whole-room|not-really': {
      heading: "Let's start with your style.",
      body: "You're designing {room} from the ground up. Establishing your style first will give Môdern a stronger foundation for everything that follows.",
      cta: 'Find My Style',
      url: '/pages/style-quiz',
      tool: 'style_quiz',
      destination_type: 'tool'
    },
    'whole-room|help-discover': {
      heading: "Let's discover your style together.",
      body: "A few quick questions will help Môdern understand what you're drawn to, so the rest of the plan for {room} starts on solid ground.",
      cta: 'Find My Style',
      url: '/pages/style-quiz',
      tool: 'style_quiz',
      destination_type: 'tool'
    },
    'refresh|yes': {
      heading: "Let's build your mood board.",
      body: "You already know your style — now let's see how new pieces work alongside what you're keeping in {room}.",
      cta: 'Create My Mood Board',
      url: '/pages/mood-board',
      tool: 'mood_board',
      destination_type: 'tool'
    },
    'refresh|not-really': {
      heading: "Let's find your style first.",
      body: "Before refreshing {room}, a quick style match will help Môdern suggest pieces that actually belong together.",
      cta: 'Find My Style',
      url: '/pages/style-quiz',
      tool: 'style_quiz',
      destination_type: 'tool'
    },
    'refresh|help-discover': {
      heading: "Let's find your style first.",
      body: "Before refreshing {room}, a quick style match will help Môdern suggest pieces that actually belong together.",
      cta: 'Find My Style',
      url: '/pages/style-quiz',
      tool: 'style_quiz',
      destination_type: 'tool'
    },
    /* 'solve-problem|layout' is intentionally NOT listed here — see
       LAYOUT ROUTING below, which is room-aware. */
    'solve-problem|color': {
      heading: "Let's build your color palette.",
      body: 'A coordinated palette gives every other decision in {room} something to work with.',
      cta: 'Generate My Palette',
      url: '/pages/color-palette',
      tool: 'color_palette',
      destination_type: 'tool'
    },
    'solve-problem|rug': {
      heading: "Let's solve the rug first.",
      body: 'The right rug size and placement can change the proportions of the entire room. The Perfect Rug Guide walks you through sizing, shape, placement, material, and the most common mistakes before you buy.',
      cta: 'Explore the Rug Guide',
      url: '/products/the-perfect-rug-guide',
      tool: 'rug_guide',
      destination_type: 'guide'
    },
    'solve-problem|lighting': {
      heading: "Let's solve the lighting first.",
      body: "Most rooms don't have a lighting problem — they have a layering problem. The Complete Home Lighting Guide walks you through how to layer light, choose fixtures, and read the natural light in {room} before you buy anything.",
      cta: 'Explore the Lighting Guide',
      url: '/products/the-complete-home-lighting-guide',
      tool: 'lighting_guide',
      destination_type: 'guide'
    },
    'solve-problem|art': {
      heading: "Let's solve the art first.",
      body: 'Art is the piece most people feel least confident about — sizing it, arranging it, and getting it on the wall. The Art & Gallery Wall Guide walks you through choosing, arranging, and hanging art you’ll actually love in {room}.',
      cta: 'Explore the Art & Gallery Wall Guide',
      url: '/products/the-art-gallery-wall-guide',
      tool: 'art_guide',
      destination_type: 'guide'
    },
    'solve-problem|furniture': {
      heading: "Let's find the right piece.",
      body: 'Môdern Match can suggest furniture that fits the style and budget of the rest of {room}.',
      cta: 'Shop for Your Space',
      url: '/pages/modern-match',
      tool: 'modern_match',
      destination_type: 'tool'
    },
    'solve-problem|organization': {
      heading: "Let's solve the organization first.",
      body: 'Most organization systems fail because they ask for more upkeep than a home can sustain. The Home Organization Guide walks you through simple systems that actually hold up in {room}.',
      cta: 'Explore the Organization Guide',
      url: '/products/the-home-organization-guide',
      tool: 'organization_guide',
      destination_type: 'guide'
    },
    'solve-problem|something-else': {
      heading: "Let's find a starting point.",
      body: "Some problems don't fit neatly into a category. Browse our Design Tips for quick answers to common questions, or explore Môdern Studio to work through {room} step by step.",
      cta: 'Browse Design Tips',
      url: '/pages/modern-studio',
      tool: 'concierge',
      destination_type: 'concierge',
      action: 'concierge'
    },
    /* "Find something to buy" asks about SCOPE (one piece vs. a coordinated
       look) rather than category — see the conditional config in the
       section's JSON block. Room is interpolated into copy only. */
    'find-buy|one-piece': {
      heading: "Let's find the right piece for {room}.",
      body: 'Product Finder can search our full catalog in plain language to find exactly what you’re picturing.',
      cta: 'Find It',
      url: '/pages/product-finder',
      tool: 'product_finder',
      destination_type: 'tool'
    },
    'find-buy|coordinated-look': {
      heading: "Let's build a coordinated look for {room}.",
      body: 'Môdern Match can put together a full set — furniture, layout logic, and finishing touches — built around your style and budget.',
      cta: 'Shop for Your Space',
      url: '/pages/modern-match',
      tool: 'modern_match',
      destination_type: 'tool'
    }
  };

  /* ==========================================================================
     3. LAYOUT ROUTING — room-aware, temporary fallback
     "Solve one problem → Layout" doesn't have a confirmed-safe purpose-built
     tool yet (Space Planner's production status is unconfirmed as of this
     writing — see the Sept 2026 security/status review). Until it's
     confirmed:
       - Living Room routes to The Living Room Guide (a genuinely strong
         match — it's specifically about planning a living room layout).
       - Every other room opens Design Tips on that room's layout tip
         (Sep 28: formerly "Design Concierge"), since no room-agnostic
         layout guide currently exists.

     Sep 28, 2026 (draft for review): approved. Its production status was
     unconfirmed when this flag was added; since then its AI+ check has moved
     to the Shopify App Proxy (Phase 4D.19), and the tool is already linked
     from the homepage tools grid and Môdern Studio.
     Routing is now ROOM-AWARE: only rooms the Space Planner actually
     supports go there (SPACE_PLANNER_ROOMS). Other rooms (dining room,
     entryway, whole home) still open Design Tips on that room's layout tip.
     The living room keeps The Living Room Guide as a secondary link.
     ========================================================================== */
  var SPACE_PLANNER_APPROVED_FOR_LAYOUT = true;
  var SPACE_PLANNER_ROOMS = ['living-room', 'bedroom', 'kitchen', 'bathroom', 'home-office', 'outdoor'];

  var SPACE_PLANNER_RECOMMENDATION = {
    heading: "Let's plan your layout.",
    body: 'Space Planner can size furniture right for {room} based on your actual dimensions.',
    cta: 'Plan My Space',
    url: '/pages/space-planner',
    tool: 'space_planner',
    destination_type: 'tool'
  };

  var LIVING_ROOM_GUIDE_RECOMMENDATION = {
    heading: "Let's solve the layout first.",
    body: 'Most living room mistakes are planning mistakes, made before a single piece is bought. The Living Room Guide walks you through planning a layout that works — traffic flow, furniture placement, and TV placement — before you shop.',
    cta: 'Explore the Living Room Guide',
    url: '/products/the-living-room-guide',
    tool: 'living_room_guide',
    destination_type: 'guide'
  };

  var LAYOUT_CONCIERGE_FALLBACK = {
    heading: "Let's start with the layout basics.",
    body: "A full floor plan for {room} isn't available yet. In the meantime, our Design Tips cover the key spacing and measurement guidelines to get the layout right.",
    cta: 'See Layout Tips',
    url: '/pages/modern-studio',
    tool: 'concierge',
    destination_type: 'concierge',
    action: 'concierge',
    flagged: true
  };

  function getLayoutRecommendation(roomId) {
    if (SPACE_PLANNER_APPROVED_FOR_LAYOUT && SPACE_PLANNER_ROOMS.indexOf(roomId) !== -1) {
      if (roomId === 'living-room') {
        var withGuide = {};
        for (var k in SPACE_PLANNER_RECOMMENDATION) withGuide[k] = SPACE_PLANNER_RECOMMENDATION[k];
        withGuide.secondary = { label: 'Prefer a step-by-step guide? The Living Room Guide', url: '/products/the-living-room-guide', tool: 'living_room_guide' };
        return withGuide;
      }
      return SPACE_PLANNER_RECOMMENDATION;
    }
    if (roomId === 'living-room') return LIVING_ROOM_GUIDE_RECOMMENDATION;
    return LAYOUT_CONCIERGE_FALLBACK;
  }

  function getRecommendation(goal, conditionalValue, roomId) {
    if (goal === 'solve-problem' && conditionalValue === 'layout') {
      return getLayoutRecommendation(roomId);
    }
    return RECOMMENDATIONS[goal + '|' + conditionalValue];
  }

  /* ==========================================================================
     4. ANALYTICS
     Same GA4 dispatch pattern as snippets/affiliate-tracking.liquid: explicit
     send_to, transport_type "beacon", no PII. No new analytics platform.
     ========================================================================== */
  var GA4_ID = 'G-W0HC37CNH5';
  var DEBUG = /[?&]msgdebug=1/.test(window.location.search);

  function track(name, params) {
    params = params || {};
    params.send_to = GA4_ID;
    params.transport_type = 'beacon';
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params);
    }
    if (DEBUG && window.console) {
      console.log('[design-my-space]', name, params);
    }
  }

  /* ==========================================================================
     5. STATE + RENDER
     ========================================================================== */
  var state = { room: null, goal: null, conditionalValue: null, step: 'room' };
  var reachedResult = false;
  var ctaClicked = false;

  /* Step-context labels shown above the progress bar (visual-polish pass).
     "number" is the numbered step out of 3; the recommendation screen has
     no number, per design — it's a destination, not a fourth question. */
  var STEP_META = {
    room: { label: 'YOUR SPACE', number: 1 },
    goal: { label: 'YOUR GOAL', number: 2 },
    conditional: { label: 'ONE MORE DETAIL', number: 3 },
    result: { label: 'YOUR NEXT STEP', number: null }
  };
  var TOTAL_STEPS = 3;

  function findRoom(id) {
    for (var i = 0; i < CONFIG.rooms.length; i++) {
      if (CONFIG.rooms[i].id === id) return CONFIG.rooms[i];
    }
    return null;
  }

  function findGoal(id) {
    for (var i = 0; i < CONFIG.goals.length; i++) {
      if (CONFIG.goals[i].id === id) return CONFIG.goals[i];
    }
    return null;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (k) {
      if (k === 'class') node.className = attrs[k];
      else if (k === 'text') node.textContent = attrs[k];
      else if (attrs[k] !== undefined) node.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) {
      if (c) node.appendChild(c);
    });
    return node;
  }

  function progressFraction() {
    var order = ['room', 'goal', 'conditional'];
    var idx = order.indexOf(state.step);
    if (idx === -1) return 1; // result step — bar stays full
    return (idx + 1) / TOTAL_STEPS;
  }

  function renderShell(bodyNode) {
    panel.innerHTML = '';
    panel.className = 'dms__panel studio-quiz';

    var eyebrow = el('p', { class: 'dms__eyebrow', text: root.getAttribute('data-dms-eyebrow') || 'Design My Space' });
    var heading = el('h1', { class: 'dms__heading', text: root.getAttribute('data-dms-heading') || 'What are you working on?' });
    var sub = el('p', { class: 'dms__subheading', text: root.getAttribute('data-dms-subheading') || '' });

    var meta = STEP_META[state.step] || STEP_META.room;
    var contextText = meta.number ? (meta.label + '   ' + meta.number + ' / ' + TOTAL_STEPS) : meta.label;
    var stepContext = el('p', { class: 'dms__step-context', text: contextText });

    var progress = el('div', { class: 'dms__progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(Math.round(progressFraction() * 100)) }, [
      el('div', { class: 'dms__progress-bar', style: 'width:' + Math.round(progressFraction() * 100) + '%' })
    ]);

    if (state.step === 'room') {
      panel.appendChild(eyebrow);
      panel.appendChild(heading);
      if (sub.textContent) panel.appendChild(sub);
    }
    panel.appendChild(stepContext);
    panel.appendChild(progress);
    panel.appendChild(bodyNode);
  }

  function optionButton(opts) {
    var label = el('span', { class: 'dms-option__label', text: opts.label });
    var children = [label];
    if (opts.desc) children.push(el('span', { class: 'dms-option__desc', text: opts.desc }));
    var btn = el('button', {
      type: 'button',
      class: 'dms-option',
      role: 'radio',
      'aria-checked': 'false',
      'data-value': opts.value
    }, children);

    /* Brief, non-animated tactile confirmation: mark this option selected
       (existing gold border + beige fill) before advancing, so the visitor
       sees their choice register. No new transition/animation is added —
       this reuses the hover transition already defined on .dms-option. */
    btn.addEventListener('click', function () {
      var group = btn.parentNode;
      if (group) {
        Array.prototype.forEach.call(group.querySelectorAll('.dms-option'), function (b) {
          b.setAttribute('aria-checked', 'false');
        });
      }
      btn.setAttribute('aria-checked', 'true');
      window.setTimeout(opts.onSelect, 150);
    });
    return btn;
  }

  function backButton(onClick, label) {
    var wrap = el('div', { class: 'dms__nav' });
    var btn = el('button', { type: 'button', class: 'dms__back', text: label || 'Back' });
    btn.addEventListener('click', onClick);
    wrap.appendChild(btn);
    return wrap;
  }

  function renderRoomStep() {
    var group = el('div', { class: 'dms-options', role: 'radiogroup', 'aria-label': 'What are you working on?' });
    CONFIG.rooms.forEach(function (room) {
      group.appendChild(optionButton({
        label: room.label,
        value: room.id,
        onSelect: function () {
          state.room = room.id;
          track('design_my_space_room_selected', { room: room.id });
          state.step = 'goal';
          render();
        }
      }));
    });
    renderShell(group);
    track('design_my_space_started', {});
  }

  function renderGoalStep() {
    var wrap = el('div', {});
    var label = el('p', { class: 'dms__step-label', text: 'What would you like help with?' });
    var group = el('div', { class: 'dms-options', 'data-dms-columns': '1', role: 'radiogroup', 'aria-label': 'What would you like help with?' });
    CONFIG.goals.forEach(function (goal) {
      group.appendChild(optionButton({
        label: goal.label,
        desc: goal.description,
        value: goal.id,
        onSelect: function () {
          state.goal = goal.id;
          track('design_my_space_goal_selected', { room: state.room, goal: goal.id });
          state.step = 'conditional';
          render();
        }
      }));
    });
    wrap.appendChild(label);
    wrap.appendChild(group);
    wrap.appendChild(backButton(function () {
      state.step = 'room';
      render();
    }));
    renderShell(wrap);
  }

  function renderConditionalStep() {
    var conditional = CONFIG.conditionals[state.goal];
    if (!conditional) {
      state.conditionalValue = 'default';
      state.step = 'result';
      render();
      return;
    }
    var wrap = el('div', {});
    var label = el('p', { class: 'dms__step-label', text: conditional.question });
    var group = el('div', { class: 'dms-options', 'data-dms-columns': conditional.singleColumn ? '1' : undefined, role: 'radiogroup', 'aria-label': conditional.question });
    conditional.options.forEach(function (opt) {
      group.appendChild(optionButton({
        label: opt.label,
        value: opt.id,
        onSelect: function () {
          state.conditionalValue = opt.id;
          track('design_my_space_conditional_selected', {
            room: state.room,
            goal: state.goal,
            conditional_type: conditional.key,
            conditional_value: opt.id
          });
          state.step = 'result';
          render();
        }
      }));
    });
    wrap.appendChild(label);
    wrap.appendChild(group);
    wrap.appendChild(backButton(function () {
      state.step = 'goal';
      render();
    }));
    renderShell(wrap);
  }

  function roomPhrase(roomId) {
    var room = findRoom(roomId);
    if (!room) return 'your space';
    return room.phrase || ('your ' + room.label.toLowerCase());
  }

  /* Small selection-summary line shown above the recommendation heading
     (e.g. "LIVING ROOM · COMPLETE ROOM · STYLE DISCOVERY"). Built entirely
     from existing in-memory state and config "shortLabel" values — no new
     persistent state. Visually subordinate to the "YOUR NEXT STEP"
     step-context line above it (smaller, muted, styled via CSS). */
  function buildSelectionSummary() {
    var parts = [];
    var room = findRoom(state.room);
    if (room) parts.push(room.label.toUpperCase());

    var goal = findGoal(state.goal);
    if (goal) parts.push((goal.shortLabel || goal.label).toUpperCase());

    var conditional = CONFIG.conditionals[state.goal];
    if (conditional) {
      var opt = null;
      for (var i = 0; i < conditional.options.length; i++) {
        if (conditional.options[i].id === state.conditionalValue) {
          opt = conditional.options[i];
          break;
        }
      }
      if (opt) parts.push((opt.shortLabel || opt.label).toUpperCase());
    }

    return parts.join(' · ');
  }

  function renderResultStep() {
    var rec = getRecommendation(state.goal, state.conditionalValue, state.room);
    var wrap = el('div', {});
    var key = state.goal + '|' + state.conditionalValue;

    if (!rec) {
      rec = {
        heading: "Let's get you to the right place.",
        body: 'Môdern Studio has the full set of design tools available.',
        cta: 'Visit Môdern Studio',
        url: '/pages/modern-studio',
        tool: 'studio_hub',
        destination_type: 'tool'
      };
      wrap.appendChild(el('p', { class: 'dms__error', text: 'Design My Space: no recommendation configured for "' + key + '" — showing a safe fallback.' }));
    }

    var heading = rec.heading.replace('{room}', roomPhrase(state.room));
    var body = rec.body.replace('{room}', roomPhrase(state.room));

    wrap.appendChild(el('p', { class: 'dms-result__context', text: buildSelectionSummary() }));
    wrap.appendChild(el('h2', { class: 'dms-result__heading', text: heading }));
    wrap.appendChild(el('p', { class: 'dms-result__body', text: body }));

    var actions = el('div', { class: 'dms-result__actions' });
    var cta = el('a', { href: rec.url, class: 'button button--primary dms-cta', text: rec.cta });
    cta.addEventListener('click', function (e) {
      if (rec.action === 'concierge') {
        /* Sep 28: opens the Design Tips panel directly on the tip for this
           room (layout) or on the topic list, instead of clicking the
           desktop-only toggle. */
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('msg:design-tips:open', {
          detail: { room: state.room, topic: state.conditionalValue === 'layout' ? 'layout' : null }
        }));
      }
      ctaClicked = true;
      track('design_my_space_cta_clicked', {
        room: state.room,
        goal: state.goal,
        conditional_value: state.conditionalValue,
        destination_tool: rec.tool,
        destination_type: rec.destination_type,
        destination_url: rec.url
      });
    });
    actions.appendChild(cta);
    if (rec.secondary) {
      var secondary = el('a', { href: rec.secondary.url, class: 'dms-result__secondary', style: 'font-family:var(--studio-sans);font-size:0.85rem;color:var(--studio-ink-muted);text-decoration:underline;text-underline-offset:3px;', text: rec.secondary.label + ' →' });
      secondary.addEventListener('click', function () {
        ctaClicked = true;
        track('design_my_space_cta_clicked', {
          room: state.room,
          goal: state.goal,
          conditional_value: state.conditionalValue,
          destination_tool: rec.secondary.tool,
          destination_type: 'guide',
          destination_url: rec.secondary.url,
          placement: 'secondary'
        });
      });
      actions.appendChild(secondary);
    }
    wrap.appendChild(actions);

    var restart = el('div', { class: 'dms__nav' });
    var restartBtn = el('button', { type: 'button', class: 'dms__back', text: 'Start Over' });
    restartBtn.addEventListener('click', function () {
      state = { room: null, goal: null, conditionalValue: null, step: 'room' };
      reachedResult = false;
      ctaClicked = false;
      render();
    });
    restart.appendChild(restartBtn);
    wrap.appendChild(restart);

    renderShell(wrap);

    reachedResult = true;
    track('design_my_space_recommendation_shown', {
      room: state.room,
      goal: state.goal,
      conditional_value: state.conditionalValue,
      destination_tool: rec.tool,
      destination_type: rec.destination_type,
      flagged: rec.flagged ? 'true' : undefined
    });

    if (DEBUG && rec.flagged && window.console) {
      console.warn('[design-my-space] "' + key + '" (room: ' + state.room + ') routes to a temporary safe fallback (' + rec.tool + '). Revisit once the primary destination’s status is confirmed.');
    }
  }

  function render() {
    if (state.step === 'room') renderRoomStep();
    else if (state.step === 'goal') renderGoalStep();
    else if (state.step === 'conditional') renderConditionalStep();
    else if (state.step === 'result') renderResultStep();
  }

  /* Abandonment — fires at most once per page view, only if the visitor
     started but never reached a CTA click. Uses visibilitychange rather
     than beforeunload, which is more reliable on mobile. */
  var abandonFired = false;
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'hidden') return;
    if (abandonFired || ctaClicked) return;
    if (!state.room) return; // never actually started
    abandonFired = true;
    track('design_my_space_abandoned', {
      last_step: state.step,
      room: state.room || undefined,
      goal: state.goal || undefined,
      reached_recommendation: reachedResult ? 'true' : 'false'
    });
  });

  render();
})();
